/**
 * R268 · Feb 2026 — GST/TDS Sales lifecycle screen.
 *
 * Mobile-first alt to /app/sales — everything works on a phone:
 *  • Doc-type pills scroll horizontally on <sm.
 *  • Composer opens as a bottom-sheet-styled dialog with big
 *    48 px touch targets.
 *  • Rows collapse to cards on <md; full table on ≥md.
 *  • Live GST/TDS math re-computes on every keystroke via
 *    POST /api/v1/gst/compute (no backend persist).
 *
 * Endpoints hit:
 *   GET  /api/v1/gst/states
 *   POST /api/v1/gst/validate-gstin
 *   POST /api/v1/gst/compute
 *   GET  /api/v1/gst/workspaces/{ws}/documents
 *   POST /api/v1/gst/workspaces/{ws}/documents
 *   POST /api/v1/gst/workspaces/{ws}/documents/{id}/convert
 *   POST /api/v1/gst/workspaces/{ws}/documents/{id}/payments
 *   POST /api/v1/gst/workspaces/{ws}/documents/{id}/status
 *   GET  /api/v1/gst/workspaces/{ws}/summary
 *   GET  /api/v1/gst/workspaces/{ws}/hsn
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useUserPreferences } from "@/lib/userPrefs";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  FileText, Plus, Trash2, ArrowRight, Receipt, Wallet, Search,
  CheckCircle2, IndianRupee, RefreshCw, X, ShieldCheck, ShieldAlert,
  Eye, ShoppingCart, TrendingUp, Repeat, Download, Send, Pause, Play,
  Pencil, History, Printer, Copy, Upload, ArrowUp, ArrowDown, ArrowUpDown, FileSpreadsheet,
  Settings, Archive, Rows3, Rows2, CalendarDays, Users as UsersIcon, SlidersHorizontal,
  ExternalLink,
} from "lucide-react";
import DocumentPreview from "@/components/DocumentPreview";
import SearchableSelect from "@/components/SearchableSelect";
import ImportWizard from "@/components/ImportWizard";
import GstReportPreview from "@/pages/app/gst-sales/GstReportPreview";
import { CURRENCIES, fmtMoney } from "@/lib/currency";
// R268ff · Feb 2026 — Extracted sub-components (composer split).
import {
  DOC_TYPES, STATUS_STYLES, EMPTY_LINE, inr, StatusPill, PreviewRow,
} from "./gst-sales/shared";
import RowActions from "./gst-sales/RowActions";
import TrashTab from "./gst-sales/TrashTab";
import Composer from "./gst-sales/Composer";
import DetailDialog from "./gst-sales/DetailDialog";
import LinkedPreviewDialog from "./gst-sales/LinkedPreviewDialog";
import GstCommentsPopover from "./gst-sales/GstCommentsPopover";
import GstLinksPopover from "./gst-sales/GstLinksPopover";
import UnmatchedCustomerPopover from "./gst-sales/UnmatchedCustomerPopover";
import CustomerOverview from "./CustomerOverview";
import PaymentDialog from "./gst-sales/PaymentDialog";
import CounterSettingsDialog from "./gst-sales/CounterSettingsDialog";
import LegacyOpsDialog from "./gst-sales/LegacyOpsDialog";
import EstimatePicker from "./gst-sales/EstimatePicker";
import LinkedBadges from "./gst-sales/LinkedBadges";
import PushToTasksDialog from "./gst-sales/PushToTasksDialog";
import MultiSelectFilter from "./gst-sales/MultiSelectFilter";
import { TaskDetail } from "@/pages/app/Tasks";

export default function GstSales() {
  const navigate = useNavigate();
  const { activeWorkspaceId: ws, memberships } = useAuth();
  const currentRole = memberships?.find((m) => m.workspace_id === ws)?.role;
  const isOwner = currentRole === "workspace_owner" || currentRole === "owner";
  const isAdmin = isOwner || currentRole === "workspace_admin" || currentRole === "admin";
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("t") || "invoice";
  });
  const [q, setQ] = useState("");
  // R268p · Feb 2026 — client filter (auto-populated from existing docs).
  const [clientFilter, setClientFilter] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  // Feb 2026 R293zze · Pre-fetched chain data keyed by doc_id, so the
  // Linked column renders its badges immediately instead of showing
  // "—" until the user hovers. Populated by `/documents/chain-bulk`
  // right after each list fetch.
  const [chainByDocId, setChainByDocId] = useState({});
  // R268kk · Feb 2026 — Excel-like sort. Persist per doc-type via user
  // preferences so a user's chosen sort follows them across devices.
  const [sortKey, setSortKey] = useState("doc_date");
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  // R268mm · Feb 2026 — Multi-select filter chips (status + customer).
  // Applied client-side after `items` load so we can toggle instantly
  // without a round-trip. Reset when the doc-type tab changes.
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [customerFilter, setCustomerFilter] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerType, setComposerType] = useState("invoice");
  const [composerEditDoc, setComposerEditDoc] = useState(null);
  const [detailDoc, setDetailDoc] = useState(null);
  // Feb 2026 R293zze · RFQ/Estimate preview opened from the Linked
  // badges popover. `{ kind: "rfq"|"estimate", id }` or null.
  const [linkedPreview, setLinkedPreview] = useState(null);
  // Feb 2026 R293zze · Customer Overview popup (clicking a customer
  // name opens this dialog instead of navigating). `null` = closed.
  const [customerPopupId, setCustomerPopupId] = useState(null);
  // Feb 2026 R293zze · Row selection — click to highlight, click again
  // to clear; also supports ArrowUp / ArrowDown keyboard navigation.
  // Feb 2026 R293zzf · Multi-select via Shift-click (range) and
  // Ctrl/Cmd-click (toggle) + a bulk-action bar.
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  const [selectionAnchor, setSelectionAnchor] = useState(null);
  const selectedRowId = selectedRowIds.size === 1 ? Array.from(selectedRowIds)[0] : null;
  // Feb 2026 R293zzf · FX toggle — flip grand totals into the workspace
  // home currency on demand using each doc's stored `grand_total_home`.
  const [fxHomeView, setFxHomeView] = useState(false);

  // Feb 2026 R293zze · Arrow-key navigation across the current
  // `sortedItems`. Moved below all state declarations so the deps
  // array (which references `payOpenFor` etc.) isn't tripped by a
  // Temporal Dead Zone in the minified build.
  const keyNavItemsRef = useRef([]);
  // Feb 2026 R293zl · Open a linked task from the SO detail panel.
  const [detailTask, setDetailTask] = useState(null);
  // Feb 2026 R293zh · Standalone-Estimate → Quotation one-click.
  const [estimateConvertOpen, setEstimateConvertOpen] = useState(false);
  const [payOpenFor, setPayOpenFor] = useState(null);

  // Feb 2026 R293zze · Arrow-key navigation + Enter opens detail +
  // Shift-extend range. Declared AFTER every state the deps array
  // reads (`payOpenFor`, `detailDoc`, `customerPopupId`, …) so the
  // minified production build doesn't trip on a Temporal Dead Zone.
  // Feb 2026 — track modal open→close transitions so a dialog-closing
  // Escape doesn't also wipe the list's row selection (Radix flushSync
  // unmounts the dialog synchronously; our window handler would
  // otherwise see detailDoc already null).
  const prevModalOpenRef = useRef(false);
  const modalClosedAtRef = useRef(0);
  useEffect(() => {
    const isOpen = !!(composerOpen || detailDoc || customerPopupId || linkedPreview || payOpenFor);
    if (prevModalOpenRef.current && !isOpen) {
      modalClosedAtRef.current = Date.now();
    }
    prevModalOpenRef.current = isOpen;
  }, [composerOpen, detailDoc, customerPopupId, linkedPreview, payOpenFor]);
  useEffect(() => {
    const onKey = (e) => {
      if (!["ArrowDown", "ArrowUp", "Escape", "Enter"].includes(e.key)) return;
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || t?.isContentEditable) return;
      if (document.querySelector('[role="dialog"]') || document.querySelector('[data-state="open"][data-radix-popper-content-wrapper]')) return;
      if (composerOpen || detailDoc || customerPopupId || linkedPreview || payOpenFor) return;
      const rows = keyNavItemsRef.current || [];
      if (!rows.length) return;
      if (e.key === "Escape") {
        // If a modal was just closed (within ~250ms), swallow this
        // Escape — it was meant for the dialog, not the list. Keeps
        // row selection intact so ArrowDown continues from the opened
        // row rather than restarting at row 1.
        if (Date.now() - modalClosedAtRef.current < 250) return;
        if (selectedRowIds.size) { setSelectedRowIds(new Set()); e.preventDefault(); }
        return;
      }
      if (e.key === "Enter") {
        if (selectedRowIds.size === 1) {
          const row = rows.find((r) => r.id === Array.from(selectedRowIds)[0]);
          if (row) { setDetailDoc(row); e.preventDefault(); }
        }
        return;
      }
      const anchorId = selectedRowId || Array.from(selectedRowIds)[selectedRowIds.size - 1] || null;
      const idx = rows.findIndex((r) => r.id === anchorId);
      let next;
      if (e.key === "ArrowDown") next = rows[Math.min(idx + 1, rows.length - 1)] || rows[0];
      else next = rows[Math.max(idx - 1, 0)] || rows[0];
      if (next) {
        if (e.shiftKey) {
          const anchor = selectionAnchor || anchorId;
          const a = rows.findIndex((r) => r.id === anchor);
          const b = rows.findIndex((r) => r.id === next.id);
          const [lo, hi] = a <= b ? [a, b] : [b, a];
          setSelectedRowIds(new Set(rows.slice(lo, hi + 1).map((r) => r.id)));
        } else {
          setSelectedRowIds(new Set([next.id]));
          setSelectionAnchor(next.id);
        }
        const el = document.querySelector(`[data-testid="gst-row-${next.id}"]`);
        if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedRowIds, selectionAnchor, selectedRowId, composerOpen, detailDoc, customerPopupId, linkedPreview, payOpenFor]);
  // R268s · Feb 2026 — org billing profile drives the printable
  // preview (colours, layout, terms). Loaded once per workspace.
  // R268cc · Feb 2026 — Import wizard visibility state.
  const [importOpen, setImportOpen] = useState(false);
  // R289 · Feb 2026 — Numbering series (doc-counter) settings dialog.
  const [counterOpen, setCounterOpen] = useState(false);
  // R289b · Feb 2026 — Legacy import operations dialog (opening-balance
  // JEs + owner-only bulk-delete of every LEG-tagged import).
  const [legacyOpsOpen, setLegacyOpsOpen] = useState(false);
  const [billingProfile, setBillingProfile] = useState(null);
  // Feb 2026 R293zze · Customer-name → client-id map. Lets the list
  // link `customer_name` to the Customer Overview page when the name
  // matches a client row 1:1 (case-insensitive, trimmed).
  const [clientByName, setClientByName] = useState({});
  // Feb 2026 R287 · Fiscal-year filter. Blank = all FYs. Default to the
  // current FY on first load so the list opens on today's data.
  const [fyFilter, setFyFilter] = useState(() => {
    const d = new Date();
    const y = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  });
  // Feb 2026 R293zp · Date-range filter (overrides FY when set).
  // Blank = fall through to the FY filter.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Feb 2026 R293zp · Density toggle for list rows. 'compact' is default,
  // 'ultra' is spreadsheet-dense (10px font, zero padding). Persisted via
  // user prefs so power users keep their choice.
  const [density, setDensity] = useState("compact");

  useEffect(() => {
    if (!ws) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/gst/workspaces/${ws}/billing-profile`);
        if (!cancelled) setBillingProfile(r.data || null);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [ws]);

  // Feb 2026 R293zze · Load workspace clients once so the sales list
  // can link `customer_name` → `/app/customers/:cid` when the name
  // matches an existing client (case-insensitive, trimmed).
  useEffect(() => {
    if (!ws) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/workspaces/${ws}/clients`);
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const map = {};
        for (const c of list) {
          const nm = (c?.name || "").trim().toLowerCase();
          if (nm) map[nm] = c.id;
        }
        setClientByName(map);
      } catch (e) { /* silent — link just won't render */ }
    })();
    return () => { cancelled = true; };
  }, [ws]);

  // R268ii · Feb 2026 — Cmd/Ctrl+K anywhere on the Sales page opens
  // the composer at the current tab's doc type (invoice by default).
  // Skips when a modal is already open so it can't hijack Cmd+K search
  // inputs used by shadcn Command palettes further down.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.key === "k" || e.key === "K")) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (composerOpen || detailDoc || payOpenFor || importOpen) return;
      // Ignore when focus is inside another Cmd+K palette.
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      if (tag === "input" && document.activeElement.getAttribute("data-cmdk-input")) return;
      e.preventDefault();
      const isView = tab.startsWith("__");
      setComposerType(isView ? "invoice" : tab);
      setComposerEditDoc(null);
      setComposerOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tab, composerOpen, detailDoc, payOpenFor, importOpen]);

  const setTabAndUrl = (k) => {
    setTab(k);
    const url = new URL(window.location);
    url.searchParams.set("t", k);
    window.history.replaceState({}, "", url);
  };

  const isView = tab.startsWith("__");

  const load = useCallback(async () => {
    if (!ws) return;
    setLoading(true);
    try {
      // For view tabs (ITC / Reports / Recurring) we still refresh summary
      // for the top cards, but the sub-view manages its own data.
      const sumR = await api.get(`/gst/workspaces/${ws}/summary`);
      setSummary(sumR.data || null);
      if (!isView) {
        const listR = await api.get(`/gst/workspaces/${ws}/documents`, {
          params: { doc_type: tab, q: q || undefined,
                     client: clientFilter || undefined,
                     fy: (dateFrom || dateTo) ? undefined : (fyFilter || undefined),
                     date_from: dateFrom || undefined,
                     date_to: dateTo || undefined,
                     include_aging: true, limit: 100 },
        });
        setItems(listR.data.items || []);
        setTotal(listR.data.total || 0);
        // Feb 2026 R293zze · Preload linked-doc chain for every row in
        // one shot so the Linked column shows real badges instantly.
        // Non-blocking — a failure just means the badges fall back to
        // lazy load on hover (today's behaviour).
        const ids = (listR.data.items || []).map((it) => it.id).filter(Boolean);
        if (ids.length) {
          try {
            const chainR = await api.post(
              `/gst/workspaces/${ws}/documents/chain-bulk`,
              { ids },
            );
            setChainByDocId(chainR.data || {});
          } catch (e) {
            setChainByDocId({});
          }
        } else {
          setChainByDocId({});
        }
      } else {
        setItems([]); setTotal(0);
        setChainByDocId({});
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [ws, tab, q, clientFilter, fyFilter, dateFrom, dateTo, isView]);

  useEffect(() => { load(); }, [load]);

  // Feb 2026 R293zr · Patch a single row in-place after an action
  // (status/convert/approve/etc) so the whole list doesn't flicker
  // or lose scroll position. Falls back to a full `load()` only
  // when we can't resolve the fresh doc (e.g. row was converted
  // into a doc of a different type and no longer belongs on this tab).
  const patchRow = useCallback(async (docId) => {
    if (!ws || !docId) return;
    try {
      const r = await api.get(`/gst/workspaces/${ws}/documents/${docId}`);
      const fresh = r.data;
      if (fresh?.doc_type && fresh.doc_type !== tab) {
        setItems((prev) => prev.filter((it) => it.id !== docId));
        return;
      }
      setItems((prev) => prev.map((it) => (it.id === docId ? { ...it, ...fresh } : it)));
    } catch (e) {
      setItems((prev) => prev.filter((it) => it.id !== docId));
    }
  }, [ws, tab]);

  const removeRow = useCallback((docId) => {
    setItems((prev) => prev.filter((it) => it.id !== docId));
    setTotal((n) => Math.max(0, n - 1));
  }, []);

  // Feb 2026 R293zn · Deep-link `?open=<doc_id>` from the Estimates
  // / RFQ "Linked" column — auto-open the matching document in the
  // detail dialog, then strip the param so a refresh doesn't re-open.
  useEffect(() => {
    if (!ws) return;
    const p = new URLSearchParams(window.location.search);
    const openId = p.get("open");
    if (!openId) return;
    (async () => {
      try {
        const r = await api.get(`/gst/workspaces/${ws}/documents/${openId}`);
        setDetailDoc(r.data);
        p.delete("open");
        const nextQs = p.toString();
        const url = window.location.pathname + (nextQs ? `?${nextQs}` : "");
        window.history.replaceState({}, "", url);
      } catch (e) { /* silent */ }
    })();
  }, [ws]);

  // R268nn · Feb 2026 — Hydrate filter + sort state from user prefs on
  // tab change. Keyed per doc-type so Invoices vs Estimates each
  // remember their own view. Falls back to sensible defaults if the
  // user has never touched this tab (or is on a fresh device — server
  // prefs will win over localStorage on next reload).
  const { getPref, setPref } = useUserPreferences();
  useEffect(() => {
    const saved = getPref(`sales.view.${tab}`, {}) || {};
    setStatusFilter(new Set(Array.isArray(saved.status) ? saved.status : []));
    setCustomerFilter(new Set(Array.isArray(saved.customer) ? saved.customer : []));
    setSortKey(saved.sortKey || "doc_date");
    setSortDir(saved.sortDir || "desc");
    setDensity(saved.density === "ultra" ? "ultra" : "compact");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Persist chip + sort selections whenever they change (hook is
  // debounced 400 ms internally, so rapid toggles collapse into one
  // PATCH). Only fires once we're past the initial hydration render.
  useEffect(() => {
    if (!ws) return;
    setPref({
      sales: {
        view: {
          [tab]: {
            status: [...statusFilter],
            customer: [...customerFilter],
            sortKey, sortDir, density,
          },
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, customerFilter, sortKey, sortDir, density]);

  const stats = useMemo(() => {
    const by = summary?.by_type || {};
    return [
      { key: "estimate",   label: "Quotations",    icon: FileText, ...by.estimate },
      { key: "invoice",    label: "Invoices",     icon: Receipt,  ...by.invoice  },
      { key: "vendor_bill",label: "Vendor bills", icon: ShoppingCart, ...by.vendor_bill },
      { key: "outstanding",label: "Receivable",   icon: IndianRupee,
        amount: summary?.outstanding_receivable || 0, total: null },
      { key: "payable",    label: "Payable",      icon: IndianRupee,
        amount: summary?.outstanding_payable || 0, total: null },
    ];
  }, [summary]);

  // R268kk · Feb 2026 — Excel-like client-side sort. Uses whichever field
  // the header defines; numeric fields (`taxable`, `gst`, `total`) are
  // pulled from `totals`. Case-insensitive string compare + tabular
  // number compare so 100 doesn't sort before 20.
  //
  // R268mm · Feb 2026 — Also applies the multi-select status + customer
  // chip filters before sorting, so both interactions compose naturally.
  const sortedItems = useMemo(() => {
    let arr = [...items];
    if (statusFilter.size > 0) {
      arr = arr.filter((r) => statusFilter.has(r.status));
    }
    if (customerFilter.size > 0) {
      arr = arr.filter((r) => customerFilter.has(r.customer_name || "—"));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (row) => {
      const t = row.totals || {};
      switch (sortKey) {
        case "doc_no":       return row.doc_no || "";
        case "doc_date":     return row.doc_date || "";
        case "subject":      return (row.subject || "").toLowerCase();
        case "customer":     return (row.customer_name || "").toLowerCase();
        case "gstin":        return (row.customer_gstin || "").toLowerCase();
        case "taxable":      return Number(t.total_taxable || 0);
        case "gst":          return Number((t.cgst_total || 0) + (t.sgst_total || 0)
                                            + (t.igst_total || 0) + (t.ugst_total || 0));
        case "total":        return Number(t.grand_total || 0);
        case "currency":     return (row.currency || "INR").toUpperCase();
        case "status":       return row.status || "";
        default:             return row[sortKey] ?? "";
      }
    };
    arr.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
    return arr;
  }, [items, sortKey, sortDir, statusFilter, customerFilter]);

  // Feb 2026 R293zze · Keep the ref in sync for the arrow-key handler.
  useEffect(() => { keyNavItemsRef.current = sortedItems; }, [sortedItems]);

  // Distinct chip options from the loaded (pre-filter) items. Sorted by
  // frequency so the most-common values surface first.
  const statusOptions = useMemo(() => {
    const counts = new Map();
    for (const it of items) counts.set(it.status, (counts.get(it.status) || 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([status, count]) => ({ value: status, count }));
  }, [items]);

  const customerOptions = useMemo(() => {
    const counts = new Map();
    for (const it of items) {
      const name = it.customer_name || "—";
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ value: name, count }));
  }, [items]);

  const toggleChip = useCallback((setter, value) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
  }, []);

  // Feb 2026 R293zn · Open any linked doc (RFQ/EST/QT/SO/INV/CN) in
  // place. GST-side rows (quotation/SO/invoice/credit_note) open the
  // Detail dialog here; RFQ/standalone-estimate rows navigate to the
  // Estimates workspace since those live outside this screen.
  const openLinkedHit = useCallback(async (hit) => {
    if (!hit) return;
    const gstKinds = new Set(["quotations", "sales_orders", "invoices", "credit_notes"]);
    if (gstKinds.has(hit.kind)) {
      try {
        const r = await api.get(`/gst/workspaces/${ws}/documents/${hit.id}`);
        setDetailDoc(r.data);
      } catch (e) {
        toast.error(apiErrorMessage(e));
      }
      return;
    }
    if (hit.kind === "estimates") {
      setLinkedPreview({ kind: "estimate", id: hit.id });
      return;
    }
    if (hit.kind === "rfqs") {
      setLinkedPreview({ kind: "rfq", id: hit.id });
      return;
    }
  }, [ws]);

  // R268nn · Feb 2026 — CSV export of the currently-visible rows.
  // Respects sort + chip filters + doc-type tab. Client-side generation
  // Feb 2026 R293zzf · Bulk-action helpers. Each works on the current
  // `selectedRowIds` set and talks to the per-doc endpoints in parallel
  // — the user picks the action from the sticky floating bar.
  const selectedDocs = useMemo(
    () => sortedItems.filter((r) => selectedRowIds.has(r.id)),
    [sortedItems, selectedRowIds]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const bulkDownloadPdfs = useCallback(async () => {
    if (!selectedDocs.length) return;
    setBulkBusy(true);
    try {
      for (const d of selectedDocs) {
        try {
          const r = await api.get(
            `/gst/workspaces/${ws}/documents/${d.id}/pdf`,
            { responseType: "blob" });
          const url = URL.createObjectURL(r.data);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${d.doc_no || d.id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 2000);
          await new Promise((rs) => setTimeout(rs, 150));
        } catch (e) { /* skip */ }
      }
      toast.success(`Downloaded ${selectedDocs.length} PDF${selectedDocs.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error("Download failed"); }
    finally { setBulkBusy(false); }
  }, [selectedDocs, ws]);

  const bulkShareLinks = useCallback(async () => {
    if (!selectedDocs.length) return;
    setBulkBusy(true);
    const out = [];
    try {
      for (const d of selectedDocs) {
        try {
          const r = await api.post(`/gst/workspaces/${ws}/documents/${d.id}/share`, {});
          out.push({ doc_no: d.doc_no, url: r.data?.share_url });
        } catch (e) { /* skip */ }
      }
      if (!out.length) { toast.error("No share links were generated"); return; }
      // Copy a plain-text block of share links to clipboard so the user
      // can paste it into any email / chat.
      const txt = out.map((o) => `${o.doc_no}: ${o.url}`).join("\n");
      try { await navigator.clipboard.writeText(txt); } catch { /* ignore */ }
      toast.success(`Share links copied — ${out.length} docs ready to paste`);
    } finally { setBulkBusy(false); }
  }, [selectedDocs, ws]);

  const bulkMarkPaid = useCallback(async () => {
    const invoices = selectedDocs.filter((d) => d.doc_type === "invoice");
    if (!invoices.length) { toast.error("Select at least one invoice"); return; }
    if (!window.confirm(`Mark ${invoices.length} invoice${invoices.length === 1 ? "" : "s"} as paid?`)) return;
    setBulkBusy(true);
    let okCount = 0;
    try {
      const today = new Date().toISOString().slice(0, 10);
      for (const d of invoices) {
        const outstanding = Number(d.outstanding || 0) || (
          Number((d.totals || {}).grand_total || 0) - Number(d.paid_total || 0));
        if (outstanding <= 0) continue;
        try {
          await api.post(`/gst/workspaces/${ws}/documents/${d.id}/payments`, {
            paid_on: today,
            amount: outstanding,
            method: "bank_transfer",
            notes: "Bulk mark-paid from list",
          });
          okCount += 1;
        } catch (e) { /* skip */ }
      }
      if (okCount) {
        toast.success(`Marked ${okCount} invoice${okCount === 1 ? "" : "s"} as paid`);
        setSelectedRowIds(new Set());
        load();
      } else {
        toast.info("All selected invoices are already paid");
      }
    } finally { setBulkBusy(false); }
  }, [selectedDocs, ws, load]);

  // so no round-trip; opens as a native browser download.
  const exportCsv = useCallback(() => {
    if (!sortedItems.length) {
      toast.info("Nothing to export");
      return;
    }
    const cols = [
      ["Doc no",   (r) => r.doc_no || ""],
      ["Subject",  (r) => r.subject || ""],
      ["Date",     (r) => r.doc_date || ""],
      ["Due date", (r) => r.due_date || ""],
      ["Customer", (r) => r.customer_name || ""],
      ["GSTIN",    (r) => r.customer_gstin || ""],
      ["Currency", (r) => r.currency || r.totals?.currency || "INR"],
      ["Taxable",  (r) => Number(r.totals?.total_taxable || 0).toFixed(2)],
      ["CGST",     (r) => Number(r.totals?.cgst_total || 0).toFixed(2)],
      ["SGST",     (r) => Number(r.totals?.sgst_total || 0).toFixed(2)],
      ["IGST",     (r) => Number(r.totals?.igst_total || 0).toFixed(2)],
      ["Cess",     (r) => Number(r.totals?.cess_total || 0).toFixed(2)],
      ["Total",    (r) => Number(r.totals?.grand_total || 0).toFixed(2)],
      ["Paid",     (r) => Number(r.paid_total || 0).toFixed(2)],
      ["Balance",  (r) => Number((r.totals?.grand_total || 0) - (r.paid_total || 0)).toFixed(2)],
      ["Status",   (r) => r.status || ""],
    ];
    const escape = (v) => {
      const s = String(v ?? "");
      // Excel-safe CSV quoting.
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.map((c) => escape(c[0])).join(",");
    const body = sortedItems.map((row) => cols.map(([, get]) => escape(get(row))).join(",")).join("\n");
    // BOM makes Excel open UTF-8 with correct ₹ / non-ASCII glyphs.
    const csv = "\uFEFF" + header + "\n" + body + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    a.download = `${tab}-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(`Exported ${sortedItems.length} row${sortedItems.length === 1 ? "" : "s"}`);
  }, [sortedItems, tab]);

  // Toggle sort direction on repeat clicks; switch key otherwise.
  // Side-effect-free updaters: calling setSortDir inside setSortKey's
  // updater double-toggled under React.StrictMode (2nd click = no-op).
  const toggleSort = useCallback((key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    // Sensible default direction per column type.
    const isNum = ["taxable", "gst", "total"].includes(key);
    const isDate = key === "doc_date";
    setSortKey(key);
    setSortDir(isNum || isDate ? "desc" : "asc");
  }, [sortKey]);

  return (
    <>
      <PageHeader
        eyebrow="Revenue · GST"
        title="Sales"
        density="dense"
        description="Quotation → Sales order → Invoice → Payment → Credit note. Full Indian GST + TDS lifecycle."
        testid="gst-sales-header"
        actions={(
          <>
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 rounded px-2.5 py-1 cursor-pointer"
              data-testid="gst-sales-import-btn"
              title="Import invoices from CSV, Excel, or a Google Sheet"
            >
              <Upload className="w-3.5 h-3.5" /> Import
            </button>
            <button
              type="button"
              onClick={() => navigate("/app/items")}
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 rounded px-2.5 py-1 cursor-pointer"
              data-testid="gst-sales-catalog-btn"
              title="Open the full Item Catalog page (HSN/SAC, pricing, categories)"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Catalog
            </button>
            <button
              type="button"
              onClick={() => setCounterOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 rounded px-2.5 py-1 cursor-pointer"
              data-testid="gst-sales-numbering-btn"
              title="Set the next invoice / quotation / SO number for a fiscal year"
            >
              <Settings className="w-3.5 h-3.5" /> Numbering
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setLegacyOpsOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 rounded px-2.5 py-1 cursor-pointer"
                data-testid="gst-sales-legacy-ops-btn"
                title="Post opening balances or wipe legacy imports"
              >
                <Archive className="w-3.5 h-3.5" /> Legacy ops
              </button>
            )}
            <a
              href="/app/settings/billing-profile"
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 rounded px-2.5 py-1"
              data-testid="gst-sales-billing-profile-link"
              title="Edit the shared Billing profile & PDF template"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Billing & PDF
            </a>
          </>
        )}
      />

      <div className="px-4 sm:px-6 md:px-10 space-y-4 pt-3">

        {/* ── Summary cards (2 up on phone, up to 5 on desktop) ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.key}
                data-testid={`gst-stat-${s.key}`}
                className="rounded-xl border border-border bg-card p-4 flex flex-col gap-1"
              >
                <div className="flex items-center gap-2 text-muted-foreground text-xs">
                  <Icon className="h-3.5 w-3.5" /> {s.label}
                </div>
                <div className="text-xl font-bold tabular-nums">
                  ₹{inr(s.amount)}
                </div>
                {s.total != null && (
                  <div className="text-[11px] text-muted-foreground">
                    {s.total} docs
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Doc-type pills (horizontal scroll on phone) ── */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4 sm:mx-0 sm:px-0"
              data-testid="gst-doctype-tabs">
          {DOC_TYPES.map((t) => {
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`gst-tab-${t.key}`}
                onClick={() => setTabAndUrl(t.key)}
                className={`shrink-0 h-11 px-4 rounded-full inline-flex items-center gap-2 text-sm border transition ${
                  active
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "border-border hover:border-foreground/30"
                }`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* ── Feb 2026 R293zp · Compact two-row toolbar.
            Row 1: search + primary actions.
            Row 2: filters (status / customer / FY / date range) +
                   density toggle + export + clear. ── */}
        <div className="rounded-lg border border-border bg-card/60 px-2 py-1.5 space-y-1.5" data-testid="gst-toolbar">
          {/* Row 1 — Search + primary actions */}
          <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                data-testid="gst-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search doc no, customer, GSTIN, subject, notes, line items, comments, links…"
                className="pl-8 h-8 text-xs"
              />
            </div>
            {tab !== "payment_receipt" && !isView && (
              <Button
                data-testid="gst-new-btn"
                className="h-8 px-3 gap-1.5 shrink-0 text-xs"
                size="sm"
                title="⌘K to open"
                onClick={() => { setComposerType(tab); setComposerOpen(true); }}
              >
                <Plus className="h-3.5 w-3.5" /> New
                <kbd className="hidden md:inline text-[9px] font-mono ml-0.5 px-1 py-px rounded bg-background/20 border border-background/30">⌘K</kbd>
              </Button>
            )}
            {tab === "estimate" && !isView && (
              <Button
                data-testid="gst-from-estimate-btn"
                variant="outline"
                size="sm"
                className="h-8 px-2.5 gap-1.5 shrink-0 text-xs"
                title="One-click quotation from an approved Estimate"
                onClick={() => setEstimateConvertOpen(true)}
              >
                <FileText className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">From Estimate</span>
              </Button>
            )}
            {tab === "__recurring" && (
              <Button
                data-testid="gst-new-recurring-btn"
                size="sm"
                className="h-8 px-3 gap-1.5 shrink-0 text-xs"
                onClick={() => { setComposerType("invoice"); setComposerOpen(true); }}
              >
                <Repeat className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">New template</span>
              </Button>
            )}
            {!isView && !tab.startsWith("__") && (
              <>
                <Button
                  variant="outline" size="sm"
                  className="h-8 px-2 shrink-0"
                  title="Reporting-grade Excel with tax breakdown"
                  data-testid="gst-export-xlsx-btn"
                  onClick={async () => {
                    try {
                      const { data } = await api.get(
                        `/gst/workspaces/${ws}/documents/export.xlsx`,
                        { params: { doc_type: tab, fy: fyFilter || undefined },
                          responseType: "blob" });
                      const url = URL.createObjectURL(data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `sales-${tab}${fyFilter ? "-" + fyFilter : ""}.xlsx`;
                      document.body.appendChild(a); a.click();
                      document.body.removeChild(a);
                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
                  }}
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                </Button>
                {tab === "invoice" && (
                  <Button
                    variant="outline" size="sm"
                    className="h-8 px-2 shrink-0"
                    title="Round-trip Excel — edit and re-upload"
                    data-testid="gst-export-roundtrip-btn"
                    onClick={async () => {
                      try {
                        const { data } = await api.get(
                          `/gst/workspaces/${ws}/documents/export.xlsx`,
                          { params: { doc_type: tab, fy: fyFilter || undefined, template: true },
                            responseType: "blob" });
                        const url = URL.createObjectURL(data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `sales-roundtrip-${tab}${fyFilter ? "-" + fyFilter : ""}.xlsx`;
                        document.body.appendChild(a); a.click();
                        document.body.removeChild(a);
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                      } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
                    }}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Row 2 — Filters + density + export */}
          {!isView && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground mx-0.5 shrink-0" />
              <MultiSelectFilter
                label="Status"
                options={statusOptions.map((o) => ({ ...o, label: o.value }))}
                selected={statusFilter}
                onToggle={(v) => toggleChip(setStatusFilter, v)}
                onClear={() => setStatusFilter(new Set())}
                testid="gst-filter-status"
                placeholder="Search status…"
              />
              <MultiSelectFilter
                label="Customer"
                icon={UsersIcon}
                options={customerOptions.map((o) => ({ ...o, label: o.value }))}
                selected={customerFilter}
                onToggle={(v) => toggleChip(setCustomerFilter, v)}
                onClear={() => setCustomerFilter(new Set())}
                testid="gst-filter-customer"
                placeholder="Search customer…"
                maxHeight="max-h-80"
              />
              <Input
                data-testid="gst-client-filter"
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                placeholder="Client name contains…"
                className="h-8 text-xs max-w-[160px]"
              />
              {clientFilter && (
                <Button variant="ghost" size="sm" className="h-8 px-1.5"
                        data-testid="gst-client-filter-clear"
                        onClick={() => setClientFilter("")}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
              <select
                value={fyFilter}
                onChange={(e) => setFyFilter(e.target.value)}
                disabled={!!(dateFrom || dateTo)}
                className="h-8 border border-border bg-background rounded-md px-2 text-xs max-w-[110px] shrink-0 disabled:opacity-50"
                data-testid="gst-fy-filter"
                title={(dateFrom || dateTo) ? "Clear the date range to use FY" : "Fiscal year"}
              >
                <option value="">All FYs</option>
                {(() => {
                  const now = new Date();
                  const cy = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
                  return [cy, cy - 1, cy - 2, cy - 3].map((y) =>
                    <option key={y} value={`${y}-${String((y + 1) % 100).padStart(2, "0")}`}>FY {y}-{String((y + 1) % 100).padStart(2, "0")}</option>
                  );
                })()}
              </select>
              <div className="inline-flex items-center gap-1 shrink-0">
                <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-8 w-[128px] text-xs"
                  title="From date"
                  data-testid="gst-date-from"
                />
                <span className="text-[10px] text-muted-foreground">→</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-8 w-[128px] text-xs"
                  title="To date"
                  data-testid="gst-date-to"
                />
                {(dateFrom || dateTo) && (
                  <Button variant="ghost" size="sm" className="h-8 px-1.5"
                          onClick={() => { setDateFrom(""); setDateTo(""); }}
                          title="Clear dates"
                          data-testid="gst-date-clear">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>

              <div className="flex-1" />

              {(statusFilter.size > 0 || customerFilter.size > 0 || clientFilter || dateFrom || dateTo) && (
                <Button
                  variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground"
                  data-testid="gst-filters-clear-all"
                  onClick={() => {
                    setStatusFilter(new Set());
                    setCustomerFilter(new Set());
                    setClientFilter("");
                    setDateFrom(""); setDateTo("");
                  }}
                >
                  <X className="h-3.5 w-3.5 mr-1" /> Clear all
                </Button>
              )}

              {/* Density toggle */}
              <div className="inline-flex items-center border border-border rounded-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDensity("compact")}
                  className={"h-8 px-2 text-[11px] inline-flex items-center gap-1 transition " +
                    (density === "compact" ? "bg-foreground text-background" : "hover:bg-muted")}
                  title="Compact rows"
                  data-testid="gst-density-compact"
                >
                  <Rows3 className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setDensity("ultra")}
                  className={"h-8 px-2 text-[11px] inline-flex items-center gap-1 transition " +
                    (density === "ultra" ? "bg-foreground text-background" : "hover:bg-muted")}
                  title="Ultra-compact (spreadsheet)"
                  data-testid="gst-density-ultra"
                >
                  <Rows2 className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Feb 2026 R293zzf · Show-in-home-currency toggle. One
                  click re-renders grand totals using each doc's stored
                  fx_rate so a CFO can scan a mixed list in INR only. */}
              <button
                type="button"
                onClick={() => setFxHomeView((v) => !v)}
                className={"h-8 px-2 text-[11px] inline-flex items-center gap-1 rounded-md border transition " +
                  (fxHomeView
                    ? "bg-indigo-600 text-white border-indigo-700"
                    : "bg-background text-foreground border-border hover:bg-muted")}
                title={fxHomeView
                  ? `Showing every row converted to ${billingProfile?.home_currency || "INR"} at today's stored FX rate`
                  : `Click to show every row's grand total in ${billingProfile?.home_currency || "INR"}`}
                data-testid="gst-fx-home-toggle"
              >
                <span className="font-semibold tracking-wide">
                  {fxHomeView ? `In ${billingProfile?.home_currency || "INR"}` : "Show in home"}
                </span>
              </button>

              <Button
                variant="outline" size="sm"
                className="h-8 px-2.5 text-xs gap-1"
                data-testid="gst-export-csv"
                onClick={exportCsv}
                title={`Export ${sortedItems.length} rows to CSV`}
              >
                <Download className="h-3.5 w-3.5" />
                <span>CSV</span>
                <span className="tabular-nums opacity-70">({sortedItems.length})</span>
              </Button>
            </div>
          )}
        </div>

        {/* ── Sub-view: ITC ledger / Reports / Recurring ── */}
        {isView && tab === "__itc"       && <ItcLedgerView ws={ws} />}
        {isView && tab === "__reports"   && <ReportsView ws={ws} />}
        {isView && tab === "__recurring" && <RecurringView ws={ws} onRefresh={load} onNew={() => { setComposerType("invoice"); setComposerOpen(true); }} />}
        {isView && tab === "__currency"  && <CurrencyReportView ws={ws} homeCurrency={billingProfile?.home_currency || "INR"} />}
        {isView && tab === "__fx_recon"  && <FxReconView ws={ws} homeCurrency={billingProfile?.home_currency || "INR"} />}
        {isView && tab === "__trash"     && <TrashTab ws={ws} onReload={load} />}

        {/* ── List ── */}
        {!isView && (loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm" data-testid="gst-loading">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground border border-dashed border-border rounded-xl"
                data-testid="gst-empty">
            No {DOC_TYPES.find((d) => d.key === tab)?.label.toLowerCase()} yet.
            Tap <span className="font-semibold text-foreground">New</span> to create your first one.
          </div>
        ) : (
          <>
            {/* Feb 2026 R293zf · Invoice tab hint — nudges users
                toward "Create invoice from SO" (line-level link)
                vs. the ad-hoc path (retainers / late fees). */}
            {tab === "invoice" && (
              <div className="rounded-md border border-sky-300 bg-sky-50 dark:border-sky-800 dark:bg-sky-950/40 p-2 text-[11px] text-sky-900 dark:text-sky-100 flex items-start gap-2"
                   data-testid="gst-invoice-hint">
                <Receipt className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div>
                  <b>Tip:</b> if the work is backed by a Sales Order, open the SO and tap <b>Create invoice</b> so line items + remaining qty are tracked. The <b>New</b> button here creates a one-off invoice (retainers, late fees, sundry charges) with no SO link.
                </div>
              </div>
            )}
            {/* R293zp · Feb 2026 — Legacy chip bar removed; multi-select
                filters live in the compact toolbar's second row above. */}

            {/* R268kk · Feb 2026 — Desktop table: sortable, gridded, no-wrap,
                sticky header. Behaves like an Excel sheet. */}
            <div className="hidden md:block rounded-lg border border-border overflow-hidden">
              <div className="overflow-x-auto max-h-[72vh]" data-testid="gst-table-scroll">
                <table className={"w-full border-collapse min-w-[960px] " + (density === "ultra" ? "text-[10px]" : "text-xs")}>
                  <thead className={"bg-muted uppercase tracking-wide text-muted-foreground sticky top-0 z-10 " + (density === "ultra" ? "text-[9px]" : "text-[10px]")}>
                    <tr>
                      {[
                        { key: null,        label: "",         align: "left"  },
                        { key: "doc_no",    label: "Doc no",   align: "left"  },
                        { key: "doc_date",  label: "Date",     align: "left"  },
                        { key: "subject",   label: "Subject",  align: "left"  },
                        { key: "customer",  label: "Customer", align: "left"  },
                        { key: "gstin",     label: "GSTIN",    align: "left"  },
                        { key: "taxable",   label: "Taxable",  align: "right" },
                        { key: "gst",       label: "GST",      align: "right" },
                        { key: "total",     label: "Total",    align: "right" },
                        { key: "currency",  label: "CCY",      align: "left"  },
                        { key: "status",    label: "Status",   align: "left"  },
                        { key: "aging",     label: "Aging",    align: "left"  },
                        { key: null,        label: "Linked",   align: "left"  },
                        { key: null,        label: "Actions",  align: "right" },
                      ].map((col, idx) => {
                        const isActive = col.key && sortKey === col.key;
                        const Icon = !col.key ? null
                                    : isActive
                                      ? (sortDir === "asc" ? ArrowUp : ArrowDown)
                                      : ArrowUpDown;
                        return (
                          <th
                            key={col.label}
                            className={
                              (density === "ultra" ? "px-1.5 py-1 " : "px-3 py-1.5 ") +
                              "border-r border-border last:border-r-0 whitespace-nowrap select-none " +
                              (col.align === "right" ? "text-right" : "text-left") +
                              (col.key ? " cursor-pointer hover:bg-muted-foreground/10" : "")
                            }
                            onClick={col.key ? () => toggleSort(col.key) : undefined}
                            data-testid={col.key ? `gst-sort-${col.key}` : undefined}
                          >
                            <span className={"inline-flex items-center gap-1 " +
                                (col.align === "right" ? "justify-end w-full" : "")}>
                              {col.label}
                              {Icon && (
                                <Icon className={"h-3 w-3 " + (isActive ? "text-foreground" : "opacity-40")} />
                              )}
                            </span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {sortedItems.map((it, rowIdx) => {
                      const t = it.totals || {};
                      const gst = (t.cgst_total || 0) + (t.sgst_total || 0) +
                                    (t.igst_total || 0) + (t.ugst_total || 0);
                      const cellCls = (density === "ultra"
                                        ? "px-1.5 py-0.5 "
                                        : "px-3 py-1.5 ") +
                                      "border-t border-r border-border last:border-r-0 whitespace-nowrap overflow-hidden text-ellipsis";
                      return (
                        <tr key={it.id}
                              className={"cursor-pointer " +
                                         (selectedRowIds.has(it.id)
                                            ? "bg-primary/15 ring-1 ring-inset ring-primary/40"
                                            : (rowIdx % 2 ? "bg-muted/20 hover:bg-primary/5" : "hover:bg-primary/5"))}
                              onClick={(e) => {
                                const rows = keyNavItemsRef.current || [];
                                if (e.shiftKey && selectionAnchor) {
                                  // Range select from anchor → this row.
                                  const a = rows.findIndex((r) => r.id === selectionAnchor);
                                  const b = rows.findIndex((r) => r.id === it.id);
                                  if (a >= 0 && b >= 0) {
                                    const [lo, hi] = a <= b ? [a, b] : [b, a];
                                    setSelectedRowIds(new Set(rows.slice(lo, hi + 1).map((r) => r.id)));
                                    return;
                                  }
                                }
                                if (e.metaKey || e.ctrlKey) {
                                  // Toggle this row without clearing others.
                                  setSelectedRowIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(it.id)) next.delete(it.id);
                                    else next.add(it.id);
                                    return next;
                                  });
                                  setSelectionAnchor(it.id);
                                  return;
                                }
                                // Plain click → toggle solo.
                                setSelectedRowIds((prev) => {
                                  if (prev.size === 1 && prev.has(it.id)) return new Set();
                                  return new Set([it.id]);
                                });
                                setSelectionAnchor(it.id);
                              }}
                              data-testid={`gst-row-${it.id}`}
                              data-selected={selectedRowIds.has(it.id) ? "true" : "false"}>
                          {/* Feb 2026 R293zzg · Internal-comments icon
                              in the first column. Opens an inline
                              popover scoped to this doc. */}
                          <td className={cellCls + " w-14"}
                              onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-0.5">
                              <GstCommentsPopover ws={ws} docId={it.id}
                                                     count={it.comments_count || 0}
                                                     onChange={(newCount) => {
                                                       // Keep the list's denormalised count
                                                       // visually in sync without a full reload.
                                                       setItems((prev) => prev.map((r) =>
                                                         r.id === it.id ? { ...r, comments_count: newCount } : r));
                                                     }} />
                              <GstLinksPopover ws={ws} docId={it.id}
                                                  count={it.links_count || 0}
                                                  onChange={(newCount) => {
                                                    setItems((prev) => prev.map((r) =>
                                                      r.id === it.id ? { ...r, links_count: newCount } : r));
                                                  }} />
                            </div>
                          </td>
                          <td className={cellCls + " font-semibold"}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setDetailDoc(it); }}
                              className="flex items-center gap-1.5 text-left hover:underline underline-offset-2 decoration-primary"
                              data-testid={`gst-docno-open-${it.id}`}
                            >
                              <span className="text-primary">{it.doc_no}</span>
                              {/* Feb 2026 R293u · Version chip for
                                  estimates with multiple versions.
                                  Only shows when version > 1 so clean
                                  v1-only estimates stay quiet. */}
                              {it.doc_type === "estimate" && (it.version || 1) > 1 && (
                                <span className="text-[9px] font-semibold uppercase tracking-wide
                                                   text-violet-800 dark:text-violet-200 bg-violet-100 dark:bg-violet-950/60
                                                   border border-violet-300 dark:border-violet-800 rounded px-1"
                                         title={`Version ${it.version} of this quotation`}
                                         data-testid={`gst-version-${it.id}`}>
                                  v{it.version}
                                </span>
                              )}
                              {it.is_legacy && (
                                <span className="text-[9px] font-semibold uppercase tracking-wide
                                                   text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-950/60
                                                   border border-amber-300 dark:border-amber-800 rounded px-1"
                                         title={it.internal_ref ? `Legacy · ${it.internal_ref}` : "Legacy import"}
                                         data-testid={`gst-legacy-${it.id}`}>
                                  LEG
                                </span>
                              )}
                            </button>
                          </td>
                          <td className={cellCls + " text-muted-foreground"}>{it.doc_date}</td>
                          {/* Feb 2026 R293zze · Subject as its own column. */}
                          <td className={cellCls + " max-w-[220px] text-foreground/80"}
                              title={it.subject || ""}
                              data-testid={`gst-subject-${it.id}`}>
                            {it.subject || <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className={cellCls + " max-w-[220px]"} title={it.customer_name}>
                            {(() => {
                              // Feb 2026 · Prefer the doc's own `client_id` when
                              // it's set (user explicitly linked a client in the
                              // editor). Falls back to case-insensitive name
                              // match so legacy docs without `client_id` still
                              // link. Previously name-only lookup meant any
                              // casing/whitespace drift from the client's name
                              // broke the link even after the user re-saved
                              // with an explicit client pick.
                              const cid = it.client_id
                                || clientByName[(it.customer_name || "").trim().toLowerCase()];
                              if (!cid) {
                                if (!it.customer_name) return <span className="text-muted-foreground">—</span>;
                                return (
                                  <UnmatchedCustomerPopover
                                    ws={ws}
                                    docId={it.id}
                                    customerName={it.customer_name}
                                    onLinked={(client) => {
                                      const nm = (client.name || "").trim().toLowerCase();
                                      setClientByName((prev) => ({ ...prev, [nm]: client.id }));
                                      setCustomerPopupId(client.id);
                                    }}
                                  />
                                );
                              }
                              return (
                                <span className="inline-flex items-center gap-1 min-w-0">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setCustomerPopupId(cid); }}
                                    className="text-left text-primary hover:underline underline-offset-2 truncate"
                                    data-testid={`gst-customer-link-${it.id}`}>
                                    {it.customer_name}
                                  </button>
                                  <a
                                    href={`/app/customers/${cid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-muted-foreground hover:text-primary flex-shrink-0"
                                    title="Open customer in a new tab"
                                    data-testid={`gst-customer-newtab-${it.id}`}>
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                </span>
                              );
                            })()}
                          </td>
                          <td className={cellCls + " text-muted-foreground"}>
                            {it.customer_gstin || "—"}
                          </td>
                          <td className={cellCls + " text-right tabular-nums"}>{fmtMoney(t.total_taxable, fxHomeView ? (billingProfile?.home_currency || "INR") : (it.currency || "INR"))}</td>
                          <td className={cellCls + " text-right tabular-nums"}>{fmtMoney(gst, fxHomeView ? (billingProfile?.home_currency || "INR") : (it.currency || "INR"))}</td>
                          <td className={cellCls + " text-right tabular-nums font-semibold"}>
                            <div className="flex items-center justify-end gap-1">
                              {fxHomeView ? (
                                <span title={`Converted from ${it.currency || "INR"} at fx ${it.fx_rate || 1}`}>
                                  {fmtMoney(t.grand_total_home != null ? t.grand_total_home : Number(t.grand_total || 0) * Number(it.fx_rate || 1), billingProfile?.home_currency || "INR")}
                                </span>
                              ) : (
                                <>
                                  <span>{fmtMoney(t.grand_total, it.currency || "INR")}</span>
                                  {it.currency && it.currency !== (billingProfile?.home_currency || "INR") && (
                                    <span className="text-[9px] font-semibold uppercase tracking-wide
                                                       text-indigo-800 dark:text-indigo-200 bg-indigo-100 dark:bg-indigo-950/60
                                                       border border-indigo-300 dark:border-indigo-800 rounded px-1"
                                             title={`Document currency: ${it.currency}`}
                                             data-testid={`gst-ccy-chip-${it.id}`}>
                                      {it.currency}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                          {/* Feb 2026 R293zzf · Dedicated sortable currency column. */}
                          <td className={cellCls + " text-xs font-mono"}>
                            <span className={(it.currency && it.currency !== (billingProfile?.home_currency || "INR"))
                                              ? "text-indigo-700 dark:text-indigo-300 font-semibold"
                                              : "text-muted-foreground"}>
                              {(it.currency || "INR").toUpperCase()}
                            </span>
                          </td>
                          <td className={cellCls}><StatusPill status={it.status} approvalStatus={it.approval_status} /></td>
                          <td className={cellCls}>
                            {/* Feb 2026 R287 · Aging chip. Server enriches
                                 with outstanding + aging_bucket when
                                 include_aging=true. Muted "current" for
                                 paid/undue rows, colour-coded otherwise. */}
                            {(() => {
                              const b = it.aging_bucket || "current";
                              const d = it.days_overdue || 0;
                              if (b === "current") return <span className="text-[10px] text-muted-foreground">—</span>;
                              const tone = b === "0-30" ? "bg-amber-100 text-amber-800 border-amber-300"
                                          : b === "31-60" ? "bg-orange-100 text-orange-800 border-orange-300"
                                          : b === "61-90" ? "bg-rose-100 text-rose-800 border-rose-300"
                                          : "bg-rose-200 text-rose-900 border-rose-400";
                              return (
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold border ${tone}`}
                                  title={`Outstanding ${fmtMoney(it.outstanding || 0, it.currency || "INR")} · ${d} days past due`}
                                  data-testid={`gst-aging-${it.id}`}
                                >{b === "90+" ? "90+ d" : `${d}d`}</span>
                              );
                            })()}
                          </td>
                          <td className={cellCls.replace(" overflow-hidden text-ellipsis", "") + " overflow-visible"}>
                            <LinkedBadges
                              ws={ws}
                              docId={it.id}
                              source="gst"
                              preloaded={chainByDocId[it.id] || null}
                              onOpen={openLinkedHit}
                            />
                          </td>
                          <td className={cellCls + " text-right"}>
                            <RowActions
                              doc={it}
                              onView={() => setDetailDoc(it)}
                              onPay={() => setPayOpenFor(it)}
                              onEdit={() => {
                                setComposerType(it.doc_type);
                                setComposerEditDoc(it);
                                setComposerOpen(true);
                              }}
                              onReload={load}
                              onPatch={patchRow}
                              onRemove={removeRow}
                              ws={ws}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2">
              {sortedItems.map((it) => {
                const t = it.totals || {};
                return (
                  <div key={it.id} className="border border-border rounded-xl p-3 bg-card"
                        data-testid={`gst-card-${it.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                          {it.doc_no}
                          {it.doc_type === "estimate" && (it.version || 1) > 1 && (
                            <span className="text-[9px] font-semibold uppercase tracking-wide
                                               text-violet-800 dark:text-violet-200 bg-violet-100 dark:bg-violet-950/60
                                               border border-violet-300 dark:border-violet-800 rounded px-1"
                                     title={`Version ${it.version} of this quotation`}
                                     data-testid={`gst-version-${it.id}`}>
                              v{it.version}
                            </span>
                          )}
                          {it.is_legacy && (
                            <span className="text-[9px] font-semibold uppercase tracking-wide
                                               text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-950/60
                                               border border-amber-300 dark:border-amber-800 rounded px-1">
                              LEG
                            </span>
                          )}
                        </div>
                        {it.subject && (
                          <div className="text-[11px] text-foreground/80 truncate font-medium"
                               title={it.subject}
                               data-testid={`gst-subject-${it.id}`}>
                            {it.subject}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground truncate">
                          {(() => {
                            const cid = it.client_id
                              || clientByName[(it.customer_name || "").trim().toLowerCase()];
                            if (!cid) {
                              if (!it.customer_name) return <span>—</span>;
                              return (
                                <UnmatchedCustomerPopover
                                  ws={ws}
                                  docId={it.id}
                                  customerName={it.customer_name}
                                  compact
                                  onLinked={(client) => {
                                    const nm = (client.name || "").trim().toLowerCase();
                                    setClientByName((prev) => ({ ...prev, [nm]: client.id }));
                                    setCustomerPopupId(client.id);
                                  }}
                                />
                              );
                            }
                            return (
                              <span className="inline-flex items-center gap-1 min-w-0">
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setCustomerPopupId(cid); }}
                                  className="text-primary hover:underline underline-offset-2 truncate"
                                  data-testid={`gst-customer-link-card-${it.id}`}>
                                  {it.customer_name}
                                </button>
                                <a
                                  href={`/app/customers/${cid}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-muted-foreground hover:text-primary flex-shrink-0"
                                  title="Open customer in a new tab">
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <StatusPill status={it.status} approvalStatus={it.approval_status} />
                    </div>
                    <div className="flex items-baseline justify-between mt-2">
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <span>{it.doc_date}</span>
                        <span onClick={(e) => e.stopPropagation()} className="flex items-center gap-0.5">
                          <GstCommentsPopover ws={ws} docId={it.id}
                                                 count={it.comments_count || 0}
                                                 onChange={(newCount) => {
                                                   setItems((prev) => prev.map((r) =>
                                                     r.id === it.id ? { ...r, comments_count: newCount } : r));
                                                 }} />
                          <GstLinksPopover ws={ws} docId={it.id}
                                              count={it.links_count || 0}
                                              onChange={(newCount) => {
                                                setItems((prev) => prev.map((r) =>
                                                  r.id === it.id ? { ...r, links_count: newCount } : r));
                                              }} />
                        </span>
                      </div>
                      <div className="text-base font-bold tabular-nums flex items-center gap-1">
                        <span>{fmtMoney(t.grand_total, it.currency || "INR")}</span>
                        {it.currency && it.currency !== (billingProfile?.home_currency || "INR") && (
                          <span className="text-[9px] font-semibold uppercase tracking-wide
                                             text-indigo-800 dark:text-indigo-200 bg-indigo-100 dark:bg-indigo-950/60
                                             border border-indigo-300 dark:border-indigo-800 rounded px-1">
                            {it.currency}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2">
                      <LinkedBadges
                        ws={ws}
                        docId={it.id}
                        source="gst"
                        preloaded={chainByDocId[it.id] || null}
                        onOpen={openLinkedHit}
                        compact
                      />
                    </div>
                    <div className="flex gap-1 mt-2 -mx-1 overflow-x-auto no-scrollbar">
                      <RowActions
                        doc={it}
                        onView={() => setDetailDoc(it)}
                        onPay={() => setPayOpenFor(it)}
                        onEdit={() => {
                          setComposerType(it.doc_type);
                          setComposerEditDoc(it);
                          setComposerOpen(true);
                        }}
                        onReload={load}
                        onPatch={patchRow}
                        onRemove={removeRow}
                        ws={ws}
                        mobile
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-xs text-muted-foreground text-center py-2" data-testid="gst-total-count">
              {total} document{total === 1 ? "" : "s"}
            </div>
          </>
        ))}
      </div>

      <Composer
        open={composerOpen}
        onClose={() => { setComposerOpen(false); setComposerEditDoc(null); }}
        docType={composerType}
        ws={ws}
        editDoc={composerEditDoc}
        profile={billingProfile}
        onCreated={() => { setComposerOpen(false); setComposerEditDoc(null); load(); }}
      />

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        ws={ws}
        onDone={load}
      />

      <CounterSettingsDialog
        open={counterOpen}
        onClose={() => setCounterOpen(false)}
        ws={ws}
      />

      <LegacyOpsDialog
        open={legacyOpsOpen}
        onClose={() => setLegacyOpsOpen(false)}
        ws={ws}
        isOwner={isOwner}
        onDone={load}
      />

      <DetailDialog
        doc={detailDoc}
        ws={ws}
        profile={billingProfile}
        onClose={() => setDetailDoc(null)}
        onPatch={async (did) => {
          await patchRow(did);
          try {
            const r = await api.get(`/gst/workspaces/${ws}/documents/${did}`);
            setDetailDoc(r.data);
          } catch { /* ignore */ }
        }}
        onEdit={(d) => {
          setComposerType(d.doc_type);
          setComposerEditDoc(d);
          setDetailDoc(null);
          setComposerOpen(true);
        }}
        onOpenDoc={async (d) => {
          // Feb 2026 R293u · Switch to another version: fetch the full
          // doc (list view returned a lightweight shape without `lines`).
          try {
            const r = await api.get(`/gst/workspaces/${ws}/documents/${d.id}`);
            setDetailDoc(r.data);
          } catch { setDetailDoc(d); }
        }}
        onOpenTask={async (t) => {
          // Feb 2026 R293zl · Open the full TaskDetail popup over the
          // current SO detail so delivery workflows stay in one place.
          try {
            const r = await api.get(`/tasks/${t.id}`);
            setDetailTask(r.data);
          } catch { setDetailTask(t); }
        }}
      />

      <PaymentDialog
        doc={payOpenFor}
        ws={ws}
        profile={billingProfile}
        onClose={() => setPayOpenFor(null)}
        onDone={() => { setPayOpenFor(null); load(); }}
      />

      {/* Feb 2026 R293zh · One-click: Estimate → Quotation. */}
      <EstimatePicker
        open={estimateConvertOpen}
        onClose={() => setEstimateConvertOpen(false)}
        ws={ws}
        mode="convert"
        onConverted={async (quo) => {
          await load();
          try {
            const r = await api.get(`/gst/workspaces/${ws}/documents/${quo.id}`);
            setDetailDoc(r.data);
          } catch { setDetailDoc(quo); }
        }}
      />

      {/* Feb 2026 R293zl · Linked task popup — opened from the SO
          delivery panel's task chips so the user can edit a task's
          status / assignee without leaving the Sales screen. */}
      {detailTask && (
        <TaskDetail
          task={detailTask}
          members={[]}
          allTasks={[]}
          onClose={() => setDetailTask(null)}
          onPatch={async (patch) => {
            try {
              const r = await api.patch(`/tasks/${detailTask.id}`, patch);
              setDetailTask(r.data);
            } catch (e) { /* toast */ }
          }}
        />
      )}

      <LinkedPreviewDialog
        open={!!linkedPreview}
        ws={ws}
        kind={linkedPreview?.kind}
        docId={linkedPreview?.id}
        onClose={() => setLinkedPreview(null)}
      />

      {/* Feb 2026 R293zze · Customer Overview popup opened from the
          customer-name link in any list row. */}
      {/* Feb 2026 R293zzf · Floating bulk-action bar — appears at the
          bottom of the viewport when ≥ 1 row is selected. */}
      {selectedRowIds.size > 0 && (
        <div
          className="fixed left-1/2 bottom-6 -translate-x-1/2 z-40 flex items-center gap-1 px-3 py-2 rounded-full border border-border bg-foreground text-background shadow-2xl"
          data-testid="gst-bulk-action-bar"
        >
          <span className="text-xs font-semibold mr-2" data-testid="gst-bulk-count">
            {selectedRowIds.size} selected
          </span>
          <button
            type="button"
            onClick={bulkShareLinks}
            disabled={bulkBusy}
            className="h-7 px-2.5 text-xs inline-flex items-center gap-1 rounded-full hover:bg-background/20 disabled:opacity-60"
            title="Create share links for every selected doc and copy them to your clipboard"
            data-testid="gst-bulk-send"
          >
            <Send className="h-3 w-3" /> Send
          </button>
          <button
            type="button"
            onClick={bulkDownloadPdfs}
            disabled={bulkBusy}
            className="h-7 px-2.5 text-xs inline-flex items-center gap-1 rounded-full hover:bg-background/20 disabled:opacity-60"
            title="Download a PDF for each selected doc"
            data-testid="gst-bulk-download"
          >
            <Download className="h-3 w-3" /> Download
          </button>
          {selectedDocs.some((d) => d.doc_type === "invoice") && (
            <button
              type="button"
              onClick={bulkMarkPaid}
              disabled={bulkBusy}
              className="h-7 px-2.5 text-xs inline-flex items-center gap-1 rounded-full hover:bg-background/20 disabled:opacity-60"
              title="Record a full payment against every selected invoice"
              data-testid="gst-bulk-mark-paid"
            >
              Mark paid
            </button>
          )}
          <span className="w-px h-4 bg-background/30 mx-1" />
          <button
            type="button"
            onClick={() => setSelectedRowIds(new Set())}
            className="h-7 px-2.5 text-xs rounded-full hover:bg-background/20"
            data-testid="gst-bulk-clear"
          >
            Clear
          </button>
        </div>
      )}

      <Dialog open={!!customerPopupId} onOpenChange={(o) => !o && setCustomerPopupId(null)}>
        <DialogContent
          className="max-w-6xl w-[95vw] max-h-[90vh] overflow-auto rounded-none p-0"
          data-testid="gst-customer-popup"
        >
          {customerPopupId && (
            <div className="relative">
              <a
                href={`/app/customers/${customerPopupId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setCustomerPopupId(null)}
                className="absolute top-3 right-12 z-10 text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 px-2 py-1 border border-border rounded"
                title="Open in a new tab"
                data-testid="gst-customer-popup-newtab"
              >
                <ExternalLink className="w-3 h-3" /> New tab
              </a>
              <div className="p-4">
                <CustomerOverview cid={customerPopupId} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}



// ─── ITC Ledger sub-view ─────────────────────────────────────────

// ─── R268aa · Feb 2026 — Mini FX sparkline ──────────────────
// Lightweight inline SVG (no chart lib). Shows 90-day trendline of
// the currency vs. workspace home currency. Cached per-pair inside
// the row via useMemo so the network hit is once per page load.
function FxSparkline({ base, home }) {
  const [series, setSeries] = useState(null);
  useEffect(() => {
    if (!base || !home || base === home) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(
          `/gst/fx-rate/series?from=${base}&to=${home}&days=90`);
        if (!cancelled) setSeries(r.data.points || []);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [base, home]);
  if (base === home) return <span className="text-muted-foreground text-[10px]">home</span>;
  if (!series || series.length < 2) return <span className="text-muted-foreground text-[10px]">—</span>;
  const rates = series.map((p) => p.rate);
  const min = Math.min(...rates), max = Math.max(...rates);
  const w = 96, h = 24, pad = 2;
  const dx = rates.length > 1 ? (w - pad * 2) / (rates.length - 1) : 0;
  const dy = (r) => max === min ? h / 2
    : h - pad - ((r - min) / (max - min)) * (h - pad * 2);
  const points = rates.map((r, i) => `${pad + i * dx},${dy(r)}`).join(" ");
  const first = rates[0], last = rates[rates.length - 1];
  const pct = first ? ((last - first) / first * 100) : 0;
  const stroke = pct >= 0 ? "#10b981" : "#ef4444";
  return (
    <div className="flex items-center gap-2">
      <svg width={w} height={h} className="shrink-0">
        <polyline fill="none" stroke={stroke} strokeWidth="1.5" points={points} />
        <circle cx={pad + (rates.length - 1) * dx} cy={dy(last)} r="2" fill={stroke} />
      </svg>
      <span className={"text-[10px] tabular-nums " +
        (pct >= 0 ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300")}>
        {pct >= 0 ? "+" : ""}{pct.toFixed(1)}%
      </span>
    </div>
  );
}


// ─── R268x · Currency Report ────────────────────────────────
function CurrencyReportView({ ws, homeCurrency }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!ws) return;
    setLoading(true);
    try {
      const r = await api.get(`/gst/workspaces/${ws}/currency-report`);
      setData(r.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [ws]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="text-sm text-muted-foreground p-3">Loading currency roll-up…</div>;
  const rows = data.rows || [];
  const home = data.home_currency || homeCurrency;
  return (
    <div className="space-y-3" data-testid="gst-currency-report">
      <div className="text-xs text-muted-foreground">
        All non-void invoices grouped by billing currency. Home totals are stamped at each invoice's own FX rate.
      </div>
      {rows.length === 0 && (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded p-6 text-center">
          No invoices yet.
        </div>
      )}
      {rows.length > 0 && (
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-xs min-w-[720px]">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left p-2">Currency</th>
                <th className="text-right p-2">Invoices</th>
                <th className="text-right p-2">Gross</th>
                <th className="text-right p-2">Paid</th>
                <th className="text-right p-2">Outstanding</th>
                <th className="text-right p-2">Avg FX</th>
                <th className="text-center p-2">90-day trend</th>
                <th className="text-right p-2">Gross ({home})</th>
                <th className="text-right p-2">Outstanding ({home})</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.currency} className="border-t border-border"
                        data-testid={`gst-currency-row-${r.currency}`}>
                  <td className="p-2 font-semibold">{r.currency}
                    {r.currency === home && (
                      <span className="ml-1 text-[10px] uppercase text-emerald-700 dark:text-emerald-300">home</span>
                    )}
                  </td>
                  <td className="p-2 text-right tabular-nums">{r.count}</td>
                  <td className="p-2 text-right tabular-nums">{fmtMoney(r.gross, r.currency)}</td>
                  <td className="p-2 text-right tabular-nums text-emerald-700 dark:text-emerald-300">{fmtMoney(r.paid, r.currency)}</td>
                  <td className="p-2 text-right tabular-nums text-amber-700 dark:text-amber-300">{fmtMoney(r.outstanding, r.currency)}</td>
                  <td className="p-2 text-right tabular-nums">{r.currency === home ? "—" : Number(r.avg_fx).toFixed(4)}</td>
                  <td className="p-2"><FxSparkline base={r.currency} home={home} /></td>
                  <td className="p-2 text-right tabular-nums">{fmtMoney(r.gross_home, home)}</td>
                  <td className="p-2 text-right tabular-nums text-amber-700 dark:text-amber-300">{fmtMoney(r.outstanding_home, home)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/60 font-semibold">
              <tr>
                <td className="p-2" colSpan={7}>Grand total ({home})</td>
                <td className="p-2 text-right tabular-nums">{fmtMoney(data.grand.gross_home, home)}</td>
                <td className="p-2 text-right tabular-nums text-amber-700 dark:text-amber-300">{fmtMoney(data.grand.outstanding_home, home)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}


// ─── R268x · FX Reconciliation ──────────────────────────────
function FxReconView({ ws, homeCurrency }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!ws) return;
    setLoading(true);
    try {
      const r = await api.get(`/gst/workspaces/${ws}/fx-reconciliation`);
      setData(r.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [ws]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="text-sm text-muted-foreground p-3">Loading FX reconciliation…</div>;
  const rows = data.rows || [];
  const home = data.home_currency || homeCurrency;
  return (
    <div className="space-y-3" data-testid="gst-fx-recon">
      <div className="text-xs text-muted-foreground">
        Every foreign-currency invoice with the FX gap — <b>Expected ({home})</b> is
        <code className="mx-1">gross × invoice_fx</code>, <b>Net remitted</b> is what
        actually landed after wire fees, and <b>Gap</b> = expected − net remitted.
        Positive gap ⇒ rupee weakened or wire cost more than planned.
      </div>
      {rows.length === 0 && (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded p-6 text-center">
          No foreign-currency invoices yet.
        </div>
      )}
      {rows.length > 0 && (
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-xs min-w-[900px]">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left p-2">Invoice</th>
                <th className="text-left p-2">Customer</th>
                <th className="text-right p-2">Gross</th>
                <th className="text-right p-2">Invoice FX</th>
                <th className="text-right p-2">Expected ({home})</th>
                <th className="text-right p-2">Net remitted ({home})</th>
                <th className="text-right p-2">Wire fees ({home})</th>
                <th className="text-right p-2">Gap ({home})</th>
                <th className="text-left p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border"
                        data-testid={`gst-fx-row-${r.id}`}>
                  <td className="p-2 font-mono">{r.doc_no}<div className="text-[10px] text-muted-foreground">{r.doc_date}</div></td>
                  <td className="p-2">{r.customer_name}</td>
                  <td className="p-2 text-right tabular-nums">{fmtMoney(r.gross, r.currency)}</td>
                  <td className="p-2 text-right tabular-nums">{Number(r.invoice_fx_rate).toFixed(4)}</td>
                  <td className="p-2 text-right tabular-nums">{fmtMoney(r.expected_home, home)}</td>
                  <td className="p-2 text-right tabular-nums">
                    {r.net_remitted_home == null
                      ? <span className="text-muted-foreground">—</span>
                      : fmtMoney(r.net_remitted_home, home)}
                  </td>
                  <td className="p-2 text-right tabular-nums text-rose-700 dark:text-rose-300">
                    {r.wire_charges_home ? fmtMoney(r.wire_charges_home, home) : "—"}
                  </td>
                  <td className={"p-2 text-right tabular-nums font-semibold " +
                    (r.gap_home == null ? "text-muted-foreground"
                       : r.gap_home > 0 ? "text-rose-700 dark:text-rose-300"
                       : "text-emerald-700 dark:text-emerald-300")}>
                    {r.gap_home == null
                      ? "—"
                      : <>{fmtMoney(r.gap_home, home)}
                          {r.gap_pct != null && <div className="text-[10px] font-normal">{r.gap_pct > 0 ? "+" : ""}{r.gap_pct}%</div>}
                        </>}
                  </td>
                  <td className="p-2"><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/60 font-semibold">
              <tr>
                <td className="p-2" colSpan={4}>Totals ({home})</td>
                <td className="p-2 text-right tabular-nums">{fmtMoney(data.totals.expected_home, home)}</td>
                <td className="p-2 text-right tabular-nums">{fmtMoney(data.totals.net_remitted_home, home)}</td>
                <td className="p-2"></td>
                <td className={"p-2 text-right tabular-nums " +
                  (data.totals.gap_home > 0 ? "text-rose-700 dark:text-rose-300"
                     : "text-emerald-700 dark:text-emerald-300")}>
                  {fmtMoney(data.totals.gap_home, home)}
                </td>
                <td className="p-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}



function ItcLedgerView({ ws }) {
  const [rows, setRows] = useState([]);
  const [balance, setBalance] = useState(null);
  const [period, setPeriod] = useState("");
  const [loading, setLoading] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);

  const load = useCallback(async () => {
    if (!ws) return;
    setLoading(true);
    try {
      const r = await api.get(`/gst/workspaces/${ws}/itc-ledger`,
                                  { params: period ? { period } : {} });
      setRows(r.data.items || []);
      setBalance(r.data.balance || null);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [ws, period]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="gst-itc-view">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {["igst", "cgst", "sgst", "ugst", "cess"].map((h) => (
          <div key={h} className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{h}</div>
            <div className="text-lg font-bold tabular-nums" data-testid={`gst-itc-balance-${h}`}>
              ₹{inr(balance?.[h])}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="month" className="h-10 max-w-[180px]"
          data-testid="gst-itc-period"
          value={period} onChange={(e) => setPeriod(e.target.value)}
          placeholder="All periods"
        />
        <Button size="sm" variant="outline" onClick={load} className="h-10"
                  data-testid="gst-itc-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
        <Button size="sm" className="h-10 ml-auto"
                  data-testid="gst-itc-claim-btn"
                  onClick={() => setClaimOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Claim / reverse
        </Button>
      </div>

      {loading ? (
        <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 border border-dashed border-border rounded-xl text-center text-sm text-muted-foreground"
                data-testid="gst-itc-empty">
          No ITC entries yet. Approve a vendor bill to auto-post credit availability.
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-xs min-w-[720px]">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left p-2">Period</th>
                <th className="text-left p-2">Kind</th>
                <th className="text-right p-2">IGST</th>
                <th className="text-right p-2">CGST</th>
                <th className="text-right p-2">SGST</th>
                <th className="text-right p-2">UGST</th>
                <th className="text-right p-2">Cess</th>
                <th className="text-left p-2">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-2">{r.period}</td>
                  <td className="p-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                      r.kind === "available" ? "bg-emerald-100 text-emerald-700"
                      : r.kind === "claimed" ? "bg-blue-100 text-blue-700"
                      : r.kind === "reversed" ? "bg-rose-100 text-rose-700"
                      : "bg-muted text-muted-foreground"}`}>{r.kind}</span>
                  </td>
                  <td className="p-2 text-right tabular-nums">₹{inr(r.amount_igst)}</td>
                  <td className="p-2 text-right tabular-nums">₹{inr(r.amount_cgst)}</td>
                  <td className="p-2 text-right tabular-nums">₹{inr(r.amount_sgst)}</td>
                  <td className="p-2 text-right tabular-nums">₹{inr(r.amount_ugst)}</td>
                  <td className="p-2 text-right tabular-nums">₹{inr(r.amount_cess)}</td>
                  <td className="p-2 text-muted-foreground">
                    {r.source_bill_id ? "bill · auto" : "manual"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ItcClaimDialog
        open={claimOpen}
        onClose={() => setClaimOpen(false)}
        ws={ws}
        onSaved={() => { setClaimOpen(false); load(); }}
      />
    </div>
  );
}


function ItcClaimDialog({ open, onClose, ws, onSaved }) {
  const [kind, setKind] = useState("claimed");
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [amounts, setAmounts] = useState({ igst: 0, cgst: 0, sgst: 0, ugst: 0, cess: 0 });
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind("claimed");
    setPeriod(new Date().toISOString().slice(0, 7));
    setAmounts({ igst: 0, cgst: 0, sgst: 0, ugst: 0, cess: 0 });
    setNotes("");
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/gst/workspaces/${ws}/itc-ledger`, {
        kind, period,
        amount_igst: Number(amounts.igst) || 0,
        amount_cgst: Number(amounts.cgst) || 0,
        amount_sgst: Number(amounts.sgst) || 0,
        amount_ugst: Number(amounts.ugst) || 0,
        amount_cess: Number(amounts.cess) || 0,
        notes: notes || null,
      });
      toast.success(`ITC ${kind} recorded`);
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="gst-itc-claim-dialog">
        <DialogHeader>
          <DialogTitle>ITC entry</DialogTitle>
          <DialogDescription>
            Record a claim (3B filed), reversal (mismatch), or manual adjustment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-11" data-testid="gst-itc-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="claimed">Claimed</SelectItem>
                <SelectItem value="reversed">Reversed</SelectItem>
                <SelectItem value="adjusted">Adjusted (delta)</SelectItem>
              </SelectContent>
            </Select>
            <Input type="month" value={period} className="h-11"
                      data-testid="gst-itc-period-input"
                      onChange={(e) => setPeriod(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {["igst", "cgst", "sgst", "ugst", "cess"].map((h) => (
              <div key={h}>
                <Label className="text-[10px] uppercase text-muted-foreground">{h}</Label>
                <Input type="number" className="h-11" value={amounts[h]}
                          data-testid={`gst-itc-amount-${h}`}
                          onChange={(e) => setAmounts((a) => ({ ...a, [h]: e.target.value }))} />
              </div>
            ))}
          </div>
          <Textarea placeholder="Notes (optional)"
                       data-testid="gst-itc-notes" rows={2}
                       value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="h-11">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="h-11"
                    data-testid="gst-itc-save">
            {saving ? "Saving…" : "Save entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Reports sub-view ────────────────────────────────────────────
// R268ww · Feb 2026 — Reworked from a card grid of download buttons
// into an interactive Excel-like preview. Reports are selected via
// tab pills; each opens `GstReportPreview` which supports date
// presets + custom range + search + per-column filter chips + sort
// and download-current-filtered-view / download-full-period.
function ReportsView({ ws }) {
  const [activeKey, setActiveKey] = useState("gstr1");
  const reports = [
    { key: "gstr1",           label: "GSTR-1",       hint: "Outward supplies" },
    { key: "gstr3b",          label: "GSTR-3B",      hint: "Monthly liability" },
    { key: "tds-receivable",  label: "TDS · Recv",   hint: "TDS on our invoices" },
    { key: "tds-payable",     label: "TDS · Pay",    hint: "TDS we deducted" },
    { key: "itc",             label: "ITC summary",  hint: "Running balance" },
  ];
  return (
    <div className="space-y-3" data-testid="gst-reports-view">
      <div className="flex flex-wrap items-center gap-1 border-b border-border pb-2">
        {reports.map((r) => (
          <button key={r.key}
                  onClick={() => setActiveKey(r.key)}
                  className={"px-3 py-1.5 text-xs border rounded-none " +
                    (activeKey === r.key
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:bg-muted")}
                  data-testid={`gst-report-tab-${r.key}`}>
            <span className="font-semibold">{r.label}</span>
            <span className="ml-1.5 text-[10px] opacity-70">· {r.hint}</span>
          </button>
        ))}
      </div>
      <GstReportPreview ws={ws} reportKey={activeKey} />
    </div>
  );
}


// ─── Recurring sub-view ─────────────────────────────────────────
function RecurringView({ ws, onRefresh, onNew }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!ws) return;
    setLoading(true);
    try {
      const r = await api.get(`/gst/workspaces/${ws}/recurring`);
      setItems(r.data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [ws]);
  useEffect(() => { load(); }, [load]);

  const action = async (rid, path, msg) => {
    try {
      await api.post(`/gst/workspaces/${ws}/recurring/${rid}/${path}`, {});
      toast.success(msg);
      load(); onRefresh?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="space-y-3" data-testid="gst-recurring-view">
      {loading ? (
        <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-10 border border-dashed border-border rounded-xl text-center text-sm text-muted-foreground"
                data-testid="gst-recurring-empty">
          No recurring templates yet.
          Tap <span className="font-semibold text-foreground">New template</span> to
          set up a monthly retainer.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} className="border border-border rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-2"
                  data-testid={`gst-recurring-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-sm truncate">{r.name}</div>
                  <StatusPill status={r.status} />
                </div>
                <div className="text-xs text-muted-foreground">
                  {r.customer_name} · {r.cadence}
                  {r.cadence === "custom_days" && r.interval_days ? ` (${r.interval_days}d)` : ""}
                  {" · next "}<span className="font-medium text-foreground">{r.next_run_date}</span>
                  {r.cycles_done ? ` · ${r.cycles_done} sent` : ""}
                </div>
                {r.last_error && (
                  <div className="text-xs text-rose-600 mt-1">⚠ {r.last_error}</div>
                )}
              </div>
              <div className="flex gap-1">
                {r.status === "active" && (
                  <Button size="sm" variant="outline" className="h-9"
                            data-testid={`gst-recurring-pause-${r.id}`}
                            onClick={() => action(r.id, "pause", "Paused")}>
                    <Pause className="h-3.5 w-3.5" />
                  </Button>
                )}
                {r.status === "paused" && (
                  <Button size="sm" variant="outline" className="h-9"
                            data-testid={`gst-recurring-resume-${r.id}`}
                            onClick={() => action(r.id, "resume", "Resumed")}>
                    <Play className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button size="sm" className="h-9"
                          data-testid={`gst-recurring-run-${r.id}`}
                          onClick={() => action(r.id, "run-now", "Invoice generated")}>
                  Run now
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

